import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { contactPatchForInteraction } from "@/lib/pipelineStage";
import { edgeFunctionErrorMessage } from "@/lib/edgeFunctionError";
import { toast } from "sonner";
import { ilikeAnyFilter } from "@/lib/searchFilter";

export type LeadStatus = "new" | "contacted" | "qualified" | "converted" | "lost" | "delivered";
export type InteractionType = "whatsapp" | "email" | "call" | "note";

export interface Lead {
  id: string;
  name: string;
  /** Optional since 20261006120200 (lead from prospecting / phone only). */
  email: string | null;
  phone: string | null;
  message: string;
  source: string;
  status: LeadStatus;
  notes: string | null;
  project_type: string | null;
  client_id: string | null;
  contacted_at: string | null;
  converted_at: string | null;
  tags: string[];
  next_follow_up_at: string | null;
  next_step: string | null;
  lost_reason: string | null;
  lost_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface LeadInteraction {
  id: string;
  lead_id: string;
  type: InteractionType;
  content: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  created_by: string | null;
}

export interface CreateLeadData {
  name: string;
  /** Required on the public form (submit-lead); optional for the admin. */
  email?: string;
  phone?: string;
  message: string;
  project_type?: string;
  source?: string;
  captchaToken?: string;
  /** Honeypot value (hidden input). Humans leave it empty. */
  honeypot?: string;
  /** Ms between the form appearing and submit — submit-lead's time-trap. */
  elapsedMs?: number;
}

export interface UpdateLeadData {
  name?: string;
  email?: string | null;
  phone?: string | null;
  message?: string;
  project_type?: string | null;
  source?: string;
  status?: LeadStatus;
  notes?: string;
  contacted_at?: string;
  converted_at?: string;
  tags?: string[];
  next_follow_up_at?: string | null;
  next_step?: string | null;
  lost_reason?: string | null;
  lost_at?: string | null;
  delivered_at?: string | null;
}

export interface LeadFilters {
  status?: LeadStatus;
  source?: string;
  search?: string;
}

// Hook para listar leads (admin)
export function useLeads(filters?: LeadFilters) {
  return useQuery({
    queryKey: ["leads", filters],
    queryFn: async () => {
      let query = supabase
        .from("leads" as never)
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      if (filters?.source) {
        query = query.eq("source", filters.source);
      }

      if (filters?.search) {
        query = query.or(ilikeAnyFilter(["name", "email", "message"], filters.search));
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Lead[];
    },
  });
}

// Hook para obter um lead específico
export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ["lead", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("leads" as never)
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Lead;
    },
    enabled: !!id,
  });
}

// Hook para obter interações de um lead
export function useLeadInteractions(leadId: string | undefined) {
  return useQuery({
    queryKey: ["lead-interactions", leadId],
    queryFn: async () => {
      if (!leadId) return [];

      const { data, error } = await supabase
        .from("lead_interactions" as never)
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as LeadInteraction[];
    },
    enabled: !!leadId,
  });
}

// Hook para criar lead.
//
// Visitante anônimo (formulário público, CTA de automação): vai pela Edge
// Function `submit-lead` (service role + rate limit). O insert direto com a
// anon key falhava em RLS (`insert().select()` exige policy SELECT, que é
// só de admin) e nenhum lead era criado.
//
// Admin logado (QuickCreateLeadDialog): insert direto. Não cria mais
// projeto/orçamento automaticamente — o projeto nasce junto com a primeira
// proposta na página do lead (InlineBudgetEditor), o que evita projetos e
// rascunhos fantasmas em cada lead.
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLeadData): Promise<{ id: string }> => {
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData.session) {
        const { data: res, error } = await supabase.functions.invoke<{
          id?: string;
          error?: string;
        }>("submit-lead", {
          body: {
            name: data.name,
            email: data.email,
            phone: data.phone || null,
            message: data.message,
            project_type: data.project_type || null,
            source: data.source || "contact_form",
            turnstile_token: data.captchaToken,
            website: data.honeypot ?? "",
            elapsed_ms: data.elapsedMs,
          },
        });
        // Surface submit-lead's code (captcha_invalid, rate_limited…) as the message.
        if (error) throw new Error(await edgeFunctionErrorMessage(error, "submit_failed"));
        if (!res?.id) throw new Error(res?.error || "submit_failed");
        return { id: res.id };
      }

      const { data: lead, error } = await supabase
        .from("leads" as never)
        .insert({
          name: data.name,
          email: data.email?.trim() || null,
          phone: data.phone || null,
          message: data.message,
          project_type: data.project_type || null,
          source: data.source || "admin_manual",
          status: "new",
        } as never)
        .select("id")
        .single();

      if (error) throw error;
      return lead as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

// Hook para atualizar lead (admin)
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateLeadData }) => {
      const updateData: Record<string, unknown> = { ...data };

      // Se o status mudou para "contacted" e não tem contacted_at, definir
      if (data.status === "contacted" && !data.contacted_at) {
        updateData.contacted_at = new Date().toISOString();
      }

      // Se o status mudou para "converted" e não tem converted_at, definir
      if (data.status === "converted" && !data.converted_at) {
        updateData.converted_at = new Date().toISOString();
      }

      const { data: lead, error } = await supabase
        .from("leads" as never)
        .update(updateData as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return lead as Lead;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
      queryClient.invalidateQueries({ queryKey: ["lead-cycle-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-inbox"] });
      toast.success("Lead atualizado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao atualizar lead: " + error.message);
    },
  });
}

// Hook para deletar lead (soft delete)
export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("leads" as never)
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
      queryClient.invalidateQueries({ queryKey: ["lead-cycle-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-inbox"] });
      toast.success("Lead removido com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao remover lead: " + error.message);
    },
  });
}

// Hook para adicionar interação
export function useAddInteraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      type,
      content,
      metadata,
    }: {
      leadId: string;
      type: InteractionType;
      content: string;
      metadata?: Record<string, unknown>;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("lead_interactions" as never)
        .insert({
          lead_id: leadId,
          type,
          content,
          metadata: metadata || null,
          created_by: user?.id || null,
        } as never)
        .select()
        .single();

      if (error) throw error;

      // First real contact moves the lead out of "Novo" in the funnel:
      // deriveStage() reads contacted_at, so a WhatsApp/e-mail/call logged
      // here must stamp it (only once, and only while the lead is still new).
      if (type !== "note") {
        // The interaction is already saved, so a failure here is a warning
        // (not a thrown error that would make the operator log it twice).
        const { data: current, error: readError } = await supabase
          .from("leads" as never)
          .select("status, contacted_at")
          .eq("id", leadId)
          .maybeSingle();
        const patch = readError
          ? null
          : contactPatchForInteraction(
              type,
              current as { status: LeadStatus; contacted_at: string | null } | null,
              new Date().toISOString(),
            );
        const { error: stampError } = patch
          ? await supabase
              .from("leads" as never)
              .update(patch as never)
              .eq("id", leadId)
          : { error: null };
        if (readError || stampError) {
          toast.warning(
            "Interação salva, mas o lead não saiu de “Novo”. Recarregue e confira o estágio.",
          );
        }
      }

      return data as LeadInteraction;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["lead-interactions", variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Interação adicionada!");
    },
    onError: (error) => {
      toast.error("Erro ao adicionar interação: " + error.message);
    },
  });
}

// Hook para estatísticas de leads
export function useLeadStats() {
  return useQuery({
    queryKey: ["lead-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads" as never)
        .select("status, source, created_at")
        .is("deleted_at", null);

      if (error) throw error;

      const leads = data || [];
      const total = leads.length;
      const byStatus = leads.reduce(
        (acc, lead) => {
          acc[lead.status] = (acc[lead.status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const bySource = leads.reduce(
        (acc, lead) => {
          acc[lead.source] = (acc[lead.source] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      // Leads dos últimos 7 dias
      const last7Days = leads.filter((lead) => {
        const createdAt = new Date(lead.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return createdAt >= sevenDaysAgo;
      });

      // Taxa de conversão
      const won = (byStatus.converted || 0) + (byStatus.delivered || 0);
      const conversionRate = total > 0 ? (won / total) * 100 : 0;

      return {
        total,
        new: byStatus.new || 0,
        contacted: byStatus.contacted || 0,
        qualified: byStatus.qualified || 0,
        converted: won,
        delivered: byStatus.delivered || 0,
        lost: byStatus.lost || 0,
        bySource,
        last7Days: last7Days.length,
        conversionRate: conversionRate.toFixed(1),
      };
    },
  });
}

// Hook para tempo médio de ciclo (lead criado -> convertido), usado no
// dashboard "Cockpit" (Comando). Compara o mês corrente com o anterior.
export function useLeadCycleStats() {
  return useQuery({
    queryKey: ["lead-cycle-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads" as never)
        .select("created_at, converted_at")
        .in("status", ["converted", "delivered"])
        .is("deleted_at", null);

      if (error) throw error;

      const rows = (data || []) as { created_at: string; converted_at: string | null }[];
      const cycles = rows
        .filter((r) => r.converted_at)
        .map((r) => ({
          days:
            (new Date(r.converted_at as string).getTime() - new Date(r.created_at).getTime()) /
            86_400_000,
          convertedAt: new Date(r.converted_at as string),
        }))
        .filter((c) => c.days >= 0);

      const avg = (arr: number[]) =>
        arr.length > 0 ? arr.reduce((s, n) => s + n, 0) / arr.length : null;

      const now = new Date();
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const overallAvg = avg(cycles.map((c) => c.days));
      const thisMonthAvg = avg(
        cycles.filter((c) => c.convertedAt >= startOfThisMonth).map((c) => c.days),
      );
      const lastMonthAvg = avg(
        cycles
          .filter((c) => c.convertedAt >= startOfLastMonth && c.convertedAt < startOfThisMonth)
          .map((c) => c.days),
      );

      return {
        avgDays: overallAvg,
        deltaVsLastMonth:
          thisMonthAvg !== null && lastMonthAvg !== null ? thisMonthAvg - lastMonthAvg : null,
      };
    },
  });
}

// E-mail para o lead pela edge function send-lead-email (que também registra
// a interação e estampa contacted_at no servidor). `functions.invoke` envia o
// JWT da sessão; erro HTTP vem em `error.context` com o JSON da função.
export function useSendLeadEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; to: string; subject: string; body: string }) => {
      const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
        "send-lead-email",
        { body: input },
      );
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const json = ctx ? await ctx.json().catch(() => null) : null;
        throw new Error(json?.error || error.message || "Erro ao enviar e-mail");
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_d, v) => {
      queryClient.invalidateQueries({ queryKey: ["lead-interactions", v.leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", v.leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}
